import { type ApolloClient, useApolloClient } from '@apollo/client'
import { FetchViewSearchSqlResultDocument } from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-common'
import type { ViewQueryRecordNode } from '@salescore/core'
import { logger, TimeWindowExecutor } from '@salescore/frontend-common'
import { message } from 'antd'
import { Array } from 'effect'
import { t } from 'i18next'
import Mustache from 'mustache'

export interface SearchResultRow {
  value: string
  label: string
  meta_label?: string
}

const OLD_SEARCH_SQL_KEY_PLACEHOLDER = `{{ searchKey }}` // v0

const compileSearchSql = (
  sql: string,
  searchKeywordBeforeEscaping: string, // エスケープ前の検索キーワード
  recordNode: ViewQueryRecordNode | undefined,
  parentRecordNode: ViewQueryRecordNode | undefined,
): string => {
  // 検索文字に「\\ % _」が含まれても正しく動作するようにすべてエスケープする
  const searchKeyword = searchKeywordBeforeEscaping.replaceAll(/[\\%_]/gv, (m) => `\\${m}`).replaceAll("'", "''")

  // v0時代(deprecated)
  if (sql.includes(OLD_SEARCH_SQL_KEY_PLACEHOLDER)) {
    // eslint-disable-next-line unicorn/no-unsafe-string-replacement
    return sql.replace(OLD_SEARCH_SQL_KEY_PLACEHOLDER, searchKeyword)
  }

  // v1時代(deprecated)

  // eslint-disable-next-line prefer-named-capture-group
  const matched = /\{\{ '(.*)' \| search \}\}/v.exec(sql)
  if (matched?.[0] !== undefined && matched[1] !== undefined) {
    const [, fieldSql] = matched
    const keys = searchKeyword.split(/\s/v).filter((x) => x !== '') // 空白でsplitする
    const condition = keys.map((key) => `${fieldSql} LIKE '%${key}%'`).join(' AND ')
    // eslint-disable-next-line unicorn/no-unsafe-string-replacement
    const convertedSql = sql.replace(matched[0], condition === '' ? 'TRUE = TRUE' : condition)
    const mustached = Mustache.render(convertedSql, { record: recordNode, parentRecord: parentRecordNode })
    return mustached
  }

  // 最新版では、単純に文字列を渡してrenderする。上記のv1のように、こちら側でロジックを持たない
  // その分「searchヘルパーでLIKE文を生成」「半角スペースで複数検索」のようなロジックを、mustacheテンプレートに持たせる形になる
  const rendered = Mustache.render(sql, {
    record: recordNode,
    query: searchKeyword,
    queries: searchKeyword.split(/\s/v),
  })
  return rendered
}

export function searchRelation(client: ApolloClient<object>, organizationId: string) {
  return async (
    searchSql: string | undefined,
    searchKeyword: string,
    recordNode: ViewQueryRecordNode | undefined,
    parentRecordNode: ViewQueryRecordNode | undefined,
  ): Promise<
    | {
        values: SearchResultRow[]
        sql: string
      }
    | undefined
  > => {
    if (searchSql === undefined) {
      return undefined
    }

    const sql = compileSearchSql(searchSql, searchKeyword, recordNode, parentRecordNode)

    logger.debug(`${t(`検索中`)}...`, {
      sql,
      recordNode,
      parentRecordNode,
      searchSql,
      searchKeyword,
    })
    const { data } = await client.query({
      query: FetchViewSearchSqlResultDocument,
      variables: {
        organizationId,
        sql,
      },
    })
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const rows = (data.viewSearchSqlResult.rows ?? []) as SearchResultRow[]
    return {
      values: Array.dedupeWith(rows, (a, b) => a.value === b.value),
      sql,
    }
  }
}

interface Option {
  value: string
  label: string
}

export const useSearchQueue = ({
  setLoading,
  setOptions,
  setSql,
}: {
  setLoading: (loading: boolean) => void
  setOptions: (options: Option[]) => void
  setSql?: (sql: string) => void
}) => {
  const client = useApolloClient()

  const search = async (
    searchSql: string,
    searchKey: string,
    recordNode?: ViewQueryRecordNode,
    parentRecordNode?: ViewQueryRecordNode,
  ) => {
    try {
      setLoading(true)
      const result = await searchRelation(client, getOrganizationIdFromPath())(
        searchSql,
        searchKey,
        recordNode,
        parentRecordNode,
      )
      const options = result?.values ?? []
      setOptions(options)
      if (setSql !== undefined) {
        setSql(result?.sql ?? '')
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        void message.error(error.message)
      }
    } finally {
      setLoading(false)
    }
  }
  const executor = new TimeWindowExecutor(search, 400)

  const queueSearch = (
    sql: string,
    key: string,
    recordNode?: ViewQueryRecordNode,
    parentRecordNode?: ViewQueryRecordNode,
  ) => {
    executor.queue([sql, key, recordNode, parentRecordNode])
  }

  return {
    queueSearch,
    search,
  }
}
